// SignalRManager (updated)
import * as signalR from "@microsoft/signalr";

type EventHandler = (...args: any[]) => void;

interface ConnectionConfig {
  url: string;
  token: string;
}

class SignalRManager {
  private connections: Map<string, signalR.HubConnection> = new Map();
  private eventHandlers: Map<string, Map<string, EventHandler[]>> = new Map();
  private connectionQueue: string[] = [];
  private isConnecting: boolean = false;

  // ✅ Initialize a hub connection (queued, one at a time)
  public initialize({ url, token }: ConnectionConfig): void {
    if (this.isConnected(url) || this.isConnectingTo(url)) {
      return;
    }

    this.connectionQueue.push(url);
    this.eventHandlers.set(url, new Map());

    const startNext = async () => {
      if (this.isConnecting || this.connections.has(url)) return;

      this.isConnecting = true;

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(url, { accessTokenFactory: () => token })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Information)
        .build();

      this.connections.set(url, connection);

      connection.onclose((error) => {
        console.warn(`🔌 Connection closed for ${url}:`, error?.message);
        this.triggerEvent(url, "disconnect");
      });

      connection.onreconnected(() => {
        console.log(`🔄 Reconnected to ${url}`);
        this.triggerEvent(url, "connect");
      });

      connection.onreconnecting(() => {
        console.log(`…Reconnecting to ${url}`);
      });

      connection.on("ReceiveUpdate", (category: string, updateData: any) => {
        console.log(`📦 Update for ${category} on ${url}:`, updateData);
        this.triggerEvent(url, category, updateData);
      });

      connection.on("UserConnected", (userId: string) => {
        console.log(`👤 User connected on ${url}: ${userId}`);
        this.triggerEvent(url, "UserConnected", userId);
      });

      connection.on("UserDisconnected", (userId: string) => {
        console.log(`👤 User disconnected on ${url}: ${userId}`);
        this.triggerEvent(url, "UserDisconnected", userId);
      });

      try {
        await connection.start();
        console.log(`✅ Connected to SignalR hub: ${url}`);
        this.triggerEvent(url, "connect");

        // ✅ Log named connection status
        const status: Record<string, boolean> = {};
        for (const [hubUrl, conn] of this.connections.entries()) {
          const name = this.extractHubName(hubUrl);
          status[name] = conn.state === signalR.HubConnectionState.Connected;
        }
        console.log("🔗 Connected sockets:", status);
      } catch (error) {
        console.error(`❌ Failed to connect to ${url}:`, error);
        this.connections.delete(url);
        this.eventHandlers.delete(url);
      } finally {
        this.isConnecting = false;
        this.connectionQueue.shift();
        if (this.connectionQueue.length > 0) {
          const nextUrl = this.connectionQueue[0];
          startNext();
        }
      }
    };

    if (!this.isConnecting) {
      startNext();
    }
  }

  private extractHubName(url: string): string {
    const match = url.match(/\/hubs\/([^/?]+)/);
    return match ? match[1].replace(/Instances$/i, "") : url;
  }

  public getConnection(url: string): signalR.HubConnection | undefined {
    return this.connections.get(url);
  }

  public isConnected(url: string): boolean {
    const conn = this.connections.get(url);
    return conn?.state === signalR.HubConnectionState.Connected;
  }

  public isConnectingTo(url: string): boolean {
    const conn = this.connections.get(url);
    return conn?.state === signalR.HubConnectionState.Connecting;
  }

  public sendUpdate(url: string, category: string, payload: any): void {
    this.connections.get(url)?.invoke("SendUpdate", category, payload)
      .catch((error) =>
        console.error(`Failed to send update to ${category} on ${url}:`, error)
      );
  }

  public sendPrivateUpdate(url: string, userId: string, payload: any): void {
    this.connections.get(url)?.invoke("SendPrivateUpdate", userId, payload)
      .catch((error) =>
        console.error(`Failed to send private update to ${userId} on ${url}:`, error)
      );
  }

  public on(url: string, event: string, handler: EventHandler): void {
    const hubEvents = this.eventHandlers.get(url);
    if (!hubEvents) return;

    if (!hubEvents.has(event)) {
      hubEvents.set(event, []);
    }
    hubEvents.get(event)!.push(handler);
  }

  public off(url: string, event: string, handler: EventHandler): void {
    const hubEvents = this.eventHandlers.get(url);
    if (!hubEvents) return;

    hubEvents.set(
      event,
      (hubEvents.get(event) ?? []).filter((h) => h !== handler)
    );
  }

  private triggerEvent(url: string, event: string, ...args: any[]): void {
    const hubEvents = this.eventHandlers.get(url);
    hubEvents?.get(event)?.forEach((handler) => handler(...args));
  }

  public close(url: string): void {
    const connection = this.connections.get(url);
    if (!connection) return;

    connection.stop()
      .then(() => console.log(`Connection closed for ${url}`))
      .catch((error) => console.error(`Stop failed for ${url}:`, error));

    this.connections.delete(url);
    this.eventHandlers.delete(url);
  }

  public closeAll(): void {
    for (const url of this.connections.keys()) {
      this.close(url);
    }
  }
}

// Preserve instance across HMR in dev (client-side only)
let signalRManager: SignalRManager;
if (typeof window !== 'undefined') {
  const globalKey = '__SIGNALR_MANAGER__';
  signalRManager = (window as any)[globalKey];
  if (!signalRManager) {
    signalRManager = new SignalRManager();
    (window as any)[globalKey] = signalRManager;
  }
} else {
  // SSR fallback (if needed, though unlikely for SignalR)
  signalRManager = new SignalRManager();
}

export default signalRManager;